import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './AttachmentModal.module.css';
import { IoClose, IoLink, IoImage, IoCamera, IoDesktopOutline } from 'react-icons/io5';
import ImageUrlModal from './ImageUrlModal';
import FileUploadModal from './FileUploadModal';
import CameraModal from './CameraModal';
import ScreenShareModal from './ScreenShareModal';
import cameraManager from '../../services/cameraManager';

const AttachmentModal = ({ 
  isOpen, 
  onClose, 
  activeTab,
  onTabChange,
  onImageUrlSubmit,
  onFileUploadSubmit,
  onCameraSubmit,
  onScreenSubmit
}) => {
  // Use ref to track previous tab
  const prevTabRef = useRef(activeTab);
  const prevOpenRef = useRef(isOpen);
  
  // Ensure cleanup when modal closes or tab changes
  useEffect(() => {
    // Handle tab changes from camera
    if (prevTabRef.current === 'camera' && activeTab !== 'camera') {
      console.log('AttachmentModal: Cleanup on camera tab change');
      cameraManager.stopAllCameras(false);
    }
    
    // Handle modal close
    if (prevOpenRef.current && !isOpen) {
      console.log('AttachmentModal: Cleanup on modal close');
      cameraManager.stopAllCameras(false);
    }
    
    // Update refs
    prevTabRef.current = activeTab;
    prevOpenRef.current = isOpen;
    
    // Cleanup on unmount
    return () => {
      if (activeTab === 'camera') {
        console.log('AttachmentModal: Cleanup on unmount');
        cameraManager.stopAllCameras(false);
      }
    };
  }, [isOpen, activeTab]);

  const handleTabChange = (newTab) => {
    console.log(`Tab changing from ${activeTab} to ${newTab}`);
    
    // Stop cameras if switching away from camera tab
    if (activeTab === 'camera') {
      cameraManager.stopAllCameras(false);
    }
    
    // Change the tab
    if (activeTab !== newTab) {
      onTabChange(newTab);
    }
  };

  const handleClose = () => {
    if (activeTab === 'camera') {
      // Ensure camera cleanup happens before close
      cameraManager.stopAllCameras(false);
    }
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={handleClose}>
          <IoClose size={24} />
        </button>
        
        <h3 className={styles.modalTitle}>Add Attachments</h3>
        
        <div className={styles.tabs}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'url' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('url')}
          >
            <IoLink size={18} />
            <span>URL</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'file' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('file')}
          >
            <IoImage size={18} />
            <span>File Upload</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'camera' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('camera')}
          >
            <IoCamera size={18} />
            <span>Camera</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'screen' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('screen')}
          >
            <IoDesktopOutline size={18} />
            <span>Screen Share</span>
          </button>
        </div>
        
        <div className={styles.tabContent}>
          {activeTab === 'url' && (
            <ImageUrlModal 
              key="url-tab"
              isOpen={true}
              onClose={handleClose}
              onSubmit={onImageUrlSubmit}
              embedded={true}
            />
          )}
          
          {activeTab === 'file' && (
            <FileUploadModal
              key="file-tab"
              isOpen={true}
              onClose={handleClose}
              onSubmit={onFileUploadSubmit}
              embedded={true}
            />
          )}

          {activeTab === 'camera' && (
            <CameraModal
              key="camera-tab"
              isOpen={true}
              onClose={handleClose}
              onSubmit={onCameraSubmit}
              embedded={true}
            />
          )}

          {activeTab === 'screen' && (
            <ScreenShareModal
              key="screen-tab"
              isOpen={true}
              onClose={handleClose}
              onSubmit={onScreenSubmit}
              embedded={true}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AttachmentModal;